import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Edit, Trash2, Loader2, Search, X } from 'lucide-react';

interface TherapyModality {
  id: string;
  name_en: string;
  name_cn: string;
  mechanism_en: string | null;
  mechanism_cn: string | null;
  procedures: string | null;
  session_length_minutes: number | null;
  recommended_number_of_sessions: number | null;
  certifications: string | null;
  organizations: string | null;
  evidence_level: string | null;
  stream_type: string | null;
  type: string | null;
  target_domain: string | null;
  stage_0: boolean;
  stage_1: boolean;
  stage_2: boolean;
  stage_3: boolean;
  stage_4: boolean;
  is_active: boolean;
  sort_order: number;
}

const defaultFormData = {
  name_en: '',
  name_cn: '',
  mechanism_en: '',
  mechanism_cn: '',
  procedures: '',
  session_length_minutes: null as number | null,
  recommended_number_of_sessions: null as number | null,
  certifications: '',
  organizations: '',
  evidence_level: 'B',
  stream_type: 'Psychology',
  type: 'therapy',
  target_domain: '',
  stage_0: false,
  stage_1: false,
  stage_2: false,
  stage_3: false,
  stage_4: false,
  is_active: true,
  sort_order: 0,
};

const AdminTherapyModalities = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTherapy, setEditingTherapy] = useState<TherapyModality | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterEvidence, setFilterEvidence] = useState('all');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch therapies
  const { data: therapies = [], isLoading } = useQuery({
    queryKey: ['admin-therapy-modalities'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('therapy_modalities')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name_en', { ascending: true });

      if (error) throw error;
      return data as TherapyModality[];
    },
  });

  // Filtered therapies
  const filteredTherapies = useMemo(() => {
    return therapies.filter(therapy => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = therapy.name_en.toLowerCase().includes(query) || 
                           therapy.name_cn.toLowerCase().includes(query);
        const matchesMechanism = (therapy.mechanism_en?.toLowerCase().includes(query)) ||
                                 (therapy.mechanism_cn?.toLowerCase().includes(query));
        if (!matchesName && !matchesMechanism) return false;
      }

      // Stage filter
      if (filterStage !== 'all') {
        const stageKey = `stage_${filterStage}` as keyof TherapyModality;
        if (!therapy[stageKey]) return false;
      }

      // Evidence filter
      if (filterEvidence !== 'all' && therapy.evidence_level !== filterEvidence) {
        return false;
      }

      return true;
    });
  }, [therapies, searchQuery, filterStage, filterEvidence]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await supabase
        .from('therapy_modalities')
        .insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-therapy-modalities'] });
      queryClient.invalidateQueries({ queryKey: ['therapy-modalities'] });
      toast({ title: 'Success', description: 'Therapy modality created successfully' });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create therapy modality', variant: 'destructive' });
      console.error(error);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const { error } = await supabase
        .from('therapy_modalities')
        .update(data)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-therapy-modalities'] });
      queryClient.invalidateQueries({ queryKey: ['therapy-modalities'] });
      toast({ title: 'Success', description: 'Therapy modality updated successfully' });
      resetForm();
      setIsDialogOpen(false);
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update therapy modality', variant: 'destructive' });
      console.error(error);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('therapy_modalities')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-therapy-modalities'] });
      queryClient.invalidateQueries({ queryKey: ['therapy-modalities'] });
      toast({ title: 'Success', description: 'Therapy modality deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete therapy modality', variant: 'destructive' });
      console.error(error);
    },
  });

  const resetForm = () => {
    setFormData(defaultFormData);
    setEditingTherapy(null);
  };

  const handleEdit = (therapy: TherapyModality) => {
    setEditingTherapy(therapy);
    setFormData({
      name_en: therapy.name_en,
      name_cn: therapy.name_cn,
      mechanism_en: therapy.mechanism_en || '',
      mechanism_cn: therapy.mechanism_cn || '',
      procedures: therapy.procedures || '',
      session_length_minutes: therapy.session_length_minutes,
      recommended_number_of_sessions: therapy.recommended_number_of_sessions,
      certifications: therapy.certifications || '',
      organizations: therapy.organizations || '',
      evidence_level: therapy.evidence_level || 'B',
      stream_type: therapy.stream_type || 'Psychology',
      type: therapy.type || 'therapy',
      target_domain: therapy.target_domain || '',
      stage_0: therapy.stage_0,
      stage_1: therapy.stage_1,
      stage_2: therapy.stage_2,
      stage_3: therapy.stage_3,
      stage_4: therapy.stage_4,
      is_active: therapy.is_active,
      sort_order: therapy.sort_order,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this therapy modality?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTherapy) {
      updateMutation.mutate({ id: editingTherapy.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) resetForm();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStage('all');
    setFilterEvidence('all');
  };

  const hasActiveFilters = searchQuery !== '' || filterStage !== 'all' || filterEvidence !== 'all';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        Loading therapy modalities...
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Therapy Modalities Management</h1>
          <p className="text-muted-foreground">{filteredTherapies.length} of {therapies.length} therapies</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Therapy
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingTherapy ? 'Edit Therapy Modality' : 'Add New Therapy Modality'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name_en">Name (English) *</Label>
                  <Input
                    id="name_en"
                    value={formData.name_en}
                    onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="name_cn">Name (Chinese) *</Label>
                  <Input
                    id="name_cn"
                    value={formData.name_cn}
                    onChange={(e) => setFormData({ ...formData, name_cn: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mechanism_en">Mechanism (English)</Label>
                  <Textarea
                    id="mechanism_en"
                    value={formData.mechanism_en}
                    onChange={(e) => setFormData({ ...formData, mechanism_en: e.target.value })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label htmlFor="mechanism_cn">Mechanism (Chinese)</Label>
                  <Textarea
                    id="mechanism_cn"
                    value={formData.mechanism_cn}
                    onChange={(e) => setFormData({ ...formData, mechanism_cn: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="procedures">Procedures</Label>
                <Textarea
                  id="procedures"
                  value={formData.procedures}
                  onChange={(e) => setFormData({ ...formData, procedures: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="session_length_minutes">Session Length (min)</Label>
                  <Input
                    id="session_length_minutes"
                    type="number"
                    value={formData.session_length_minutes || ''}
                    onChange={(e) => setFormData({ ...formData, session_length_minutes: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
                <div>
                  <Label htmlFor="recommended_sessions">Recommended Sessions</Label>
                  <Input
                    id="recommended_sessions"
                    type="number"
                    value={formData.recommended_number_of_sessions || ''}
                    onChange={(e) => setFormData({ ...formData, recommended_number_of_sessions: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
                <div>
                  <Label htmlFor="evidence_level">Evidence Level</Label>
                  <Select
                    value={formData.evidence_level}
                    onValueChange={(value) => setFormData({ ...formData, evidence_level: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A">A - Strong</SelectItem>
                      <SelectItem value="B">B - Moderate</SelectItem>
                      <SelectItem value="C">C - Limited</SelectItem>
                      <SelectItem value="D">D - Emerging</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="stream_type">Stream Type</Label>
                  <Select
                    value={formData.stream_type}
                    onValueChange={(value) => setFormData({ ...formData, stream_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Psychology">Psychology</SelectItem>
                      <SelectItem value="Energy Healing">Energy Healing</SelectItem>
                      <SelectItem value="Bodywork">Bodywork</SelectItem>
                      <SelectItem value="Spiritual">Spiritual</SelectItem>
                      <SelectItem value="Traditional">Traditional</SelectItem>
                      <SelectItem value="Integrative">Integrative</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="certifications">Certifications</Label>
                  <Input
                    id="certifications"
                    value={formData.certifications}
                    onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="organizations">Organizations</Label>
                  <Input
                    id="organizations"
                    value={formData.organizations}
                    onChange={(e) => setFormData({ ...formData, organizations: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="target_domain">Target Domain</Label>
                  <Input
                    id="target_domain"
                    value={formData.target_domain}
                    onChange={(e) => setFormData({ ...formData, target_domain: e.target.value })}
                    placeholder="e.g., 身,意,灵"
                  />
                </div>
                <div>
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              {/* Stage Checkboxes */}
              <div className="space-y-2">
                <Label>Applicable Stages</Label>
                <div className="flex flex-wrap gap-4">
                  {[0, 1, 2, 3, 4].map((stage) => (
                    <div key={stage} className="flex items-center space-x-2">
                      <Checkbox
                        id={`stage_${stage}`}
                        checked={formData[`stage_${stage}` as keyof typeof formData] as boolean}
                        onCheckedChange={(checked) => 
                          setFormData({ ...formData, [`stage_${stage}`]: checked === true })
                        }
                      />
                      <Label htmlFor={`stage_${stage}`} className="text-sm">
                        Stage {stage}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => handleDialogOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  )}
                  {editingTherapy ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or mechanism..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStage} onValueChange={setFilterStage}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                <SelectItem value="0">Stage 0</SelectItem>
                <SelectItem value="1">Stage 1</SelectItem>
                <SelectItem value="2">Stage 2</SelectItem>
                <SelectItem value="3">Stage 3</SelectItem>
                <SelectItem value="4">Stage 4</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterEvidence} onValueChange={setFilterEvidence}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Evidence" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="A">Level A</SelectItem>
                <SelectItem value="B">Level B</SelectItem>
                <SelectItem value="C">Level C</SelectItem>
                <SelectItem value="D">Level D</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name (EN/CN)</TableHead>
                <TableHead>Stream</TableHead>
                <TableHead>Evidence</TableHead>
                <TableHead>Stages</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTherapies.map((therapy) => (
                <TableRow key={therapy.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{therapy.name_en}</div>
                      <div className="text-sm text-muted-foreground">{therapy.name_cn}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{therapy.stream_type || '-'}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      therapy.evidence_level === 'A' ? 'default' :
                      therapy.evidence_level === 'B' ? 'secondary' : 'outline'
                    }>
                      {therapy.evidence_level || '-'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {therapy.stage_0 && <Badge variant="destructive" className="text-xs">0</Badge>}
                      {therapy.stage_1 && <Badge variant="outline" className="text-xs">1</Badge>}
                      {therapy.stage_2 && <Badge variant="outline" className="text-xs">2</Badge>}
                      {therapy.stage_3 && <Badge variant="outline" className="text-xs">3</Badge>}
                      {therapy.stage_4 && <Badge variant="outline" className="text-xs">4</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {therapy.session_length_minutes ? `${therapy.session_length_minutes}min` : '-'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={therapy.is_active ? 'default' : 'secondary'}>
                      {therapy.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEdit(therapy)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(therapy.id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminTherapyModalities;
